import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import TrackedAssetContainer from '~/shared/components/TrackedAssetContainer';
import parseCustomVarAttributes from '~/shared/utils/parseCustomVarAttributes';
import { Link } from '~/techstyle-shared/next-routes';
import { Video } from '~/techstyle-shared/react-components';

import { DEFAULT_VIDEO_ASPECT_RATIO } from '../constants';
import useAssetCardTracking from '../hooks/useAssetCardTracking';

const TrackedLink = styled.a`
  display: block;
  width: 100%;
`;

const FallbackContainer = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) =>
    theme.colors.gridAssetCardFallbackContentBackgroundColor};
  color: ${({ theme }) => theme.colors.gridAssetCardFallbackContentTextColor};
`;

const getUrlFromVideoId = (videoId, defaultService = 'vimeo') => {
  const regexp = new RegExp('https?://');

  if (regexp.test(videoId)) {
    return videoId;
  }

  const [serviceName, id] = `${videoId}`.includes(':')
    ? videoId.split(':')
    : [defaultService, videoId];

  if (serviceName === 'vimeo') {
    return `https://vimeo.com/${id}`;
  } else if (serviceName === 'youtube') {
    return `https://www.youtube.com/watch?v=${id}`;
  }
};

// className is necessary for forwarding styles, e.g. for `styled(AssetCard)`
const AssetCard = ({ imageFilename, label, options, className, container }) => {
  const { imageTrackingData, trackAssetClick } = useAssetCardTracking({
    imageFilename,
    label,
    options,
    container,
  });

  if (imageFilename) {
    const extraProps = parseCustomVarAttributes(
      options?.customVars?.clickable_asset_extra_properties
    );

    return (
      <TrackedAssetContainer asset={imageTrackingData}>
        {options?.targetUrl ? (
          <Link href={options.targetUrl} className="h-full">
            <TrackedLink onClick={trackAssetClick}>
              <img
                src={imageFilename}
                alt={label}
                className={className}
                {...(extraProps || {})}
              />
            </TrackedLink>
          </Link>
        ) : (
          <img
            src={imageFilename}
            alt={label}
            className={className}
            {...(extraProps || {})}
          />
        )}
      </TrackedAssetContainer>
    );
  }

  // TODO: consider making cleaner video component and abstract away the parsing logic
  const videoId = options?.customVars?.videoId || options?.customVars?.VideoID;
  const videoUrl = getUrlFromVideoId(videoId);
  const aspectRatio =
    options?.customVars?.videoAspectRatio || DEFAULT_VIDEO_ASPECT_RATIO;
  if (videoId) {
    return <Video url={videoUrl} aspectRatio={aspectRatio} />;
  }

  // TODO: have a better fallback UI
  // Handle unsupported marketing asset types or missing user input (imageFilename | videoId)
  if (process.env.NODE_ENV !== 'production') {
    return <FallbackContainer>Marketing Asset Not available</FallbackContainer>;
  }

  return null;
};

AssetCard.propTypes = {
  className: PropTypes.string,
  container: PropTypes.shape({
    name: PropTypes.string,
  }),
  imageFilename: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.shape({
    customVars: PropTypes.shape({
      videoId: PropTypes.string,
      VideoID: PropTypes.string,
      videoAspectRatio: PropTypes.number,
      clickable_asset_extra_properties: PropTypes.string,
    }),
    targetUrl: PropTypes.string,
  }),
};

export default AssetCard;
